import { collection, doc, getDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import React, { useEffect, useRef, useState } from 'react';
import { db } from '../../services/firebaseConfig';
import { Message } from './Message';
import { SendMessage } from './SendMessage';
import { useLocation, useNavigate } from 'react-router-dom';
import arrowIconBlack from "../../images/arrowIconBlack.png";
import defaultPhotos from "../../images/defaultPhotos.png";
import moment from 'moment';
import { Skeleton } from '@mui/material'; // Import Skeleton from MUI
import boyImg from "../../images/blankPicBoy2.png";
import gitlImg from "../../images/blankPic3.png";
import { ArrowBack } from '@mui/icons-material';

const isSameDay = (date1, date2) => moment(date1).isSame(date2, 'day');

const formatDate = (timestamp) => {
    if (moment().isSame(timestamp, 'day')) return 'Today';
    if (moment().subtract(1, 'day').isSame(timestamp, 'day')) return 'Yesterday';
    return moment(timestamp).format('MMMM DD, YYYY');
}

export const Chat = () => {
    const location = useLocation();
    const { state } = location;
    const chatId = state?.chatId;
    const recipientId = state?.recipientId;
    const profile = state?.profile;
    const [messages, setMessages] = useState([]);
    const [recipientData, setRecipientData] = useState(null);
    const [typing, setTyping] = useState(false); // Typing indicator state
    const scroll = useRef();
    const navigate = useNavigate();



    const markAsRead = async (chatId) => {

        const conversationRef = doc(db, `conversations`, chatId);
        const conversationDoc = await getDoc(conversationRef);

        try {
            if (conversationDoc.exists()) {
                const data = conversationDoc.data();
                const lastMessage = data.lastMessage;

                if (lastMessage &&
                    lastMessage.senderId !== localStorage.getItem('username')
                    && lastMessage.status !== 'read'
                ) {
                    await updateDoc(conversationRef, {
                        "lastMessage.status": "read",
                    });
                }

            }
        } catch (error) {
            console.error("Error updating message status: ", error);
        }
    };
    // Fetch recipient data (like name, profile picture, last seen)
    useEffect(() => {
        if (!recipientId) return;

        const fetchRecipientData = async () => {
            const recipientRef = doc(db, 'users', recipientId);
            const recipientDoc = await getDoc(recipientRef);
            if (recipientDoc.exists()) {
                setRecipientData(recipientDoc.data());
            }
        };

        fetchRecipientData();
    }, [recipientId]);

    // Real-time listener for recipient's lastSeen updates
    useEffect(() => {
        if (!recipientId) return;

        const recipientRef = doc(db, 'users', recipientId);
        const unsubscribe = onSnapshot(recipientRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                setRecipientData(docSnapshot.data());
            }
        });

        return () => unsubscribe();
    }, [recipientId]);

    // Listen for incoming messages in the chat
    useEffect(() => {
        if (!chatId) {
            console.error('chatId is undefined');
            return;
        }
        markAsRead(chatId);
        const messagesRef = collection(db, 'conversations', chatId, 'messages');
        const q = query(messagesRef, orderBy('timestamp'));

        const unsubscribe = onSnapshot(q, async (querySnapshot) => {
            const messagesData = [];
            for (const docSnapshot of querySnapshot.docs) {
                const message = { id: docSnapshot.id, ...docSnapshot.data() };
                messagesData.push(message);

                // Update status to "delivered" if the recipient is online
                if (message.status === 'sent' && message.senderId !== localStorage.getItem('username')) {
                    await updateMessageStatus(message.id, 'delivered');
                }
            }
            setMessages(messagesData);

            // Scroll to the bottom when new messages arrive
            if (scroll.current) {
                scroll.current.scrollTo({
                    behavior: 'smooth',
                    top: scroll.current.scrollHeight - scroll.current.clientHeight,
                });
            }
        });

        return () => unsubscribe();
    }, [chatId]);

    const updateMessageStatus = async (messageId, status) => {
        const messageRef = doc(db, 'conversations', chatId, 'messages', messageId);
        await updateDoc(messageRef, { status });
    };

    // Fetch profile image, fallback to default if not available
    const profileImage = recipientData && recipientData.profilePic
        ? recipientData.profilePic
        : boyImg;

    // Display time ago in a readable format
    const timeAgo = (date) => moment(date).fromNow();

    // Typing indicator
    const handleTyping = () => {
        setTyping(true);
        setTimeout(() => setTyping(false), 3000); // Stop typing indicator after 3 seconds
    };

    const groupMessagesByDate = (messages) => {
        return messages.reduce((groups, message) => {
            const timestamp = message.timestamp?.toDate
                ? message.timestamp.toDate()
                : null;

            const date = timestamp ? moment(timestamp).format('YYYY-MM-DD') : 'unknown';

            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(message);
            return groups;
        }, {});
    };

    const groupedMessages = groupMessagesByDate(messages);

    const renderMessages = () => {
        const today = moment().format('YYYY-MM-DD');
        const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');

        return Object.keys(groupedMessages).map((date) => {
            const isToday = date === today;
            const isYesterday = date === yesterday;

            return (
                <React.Fragment key={date}>
                    {date !== 'unknown' && (
                        <div className="date-divider">
                            {isToday ? 'Today' : isYesterday ? 'Yesterday' : moment(date).format('DD MMMM YYYY')}
                        </div>
                    )}
                    {groupedMessages[date].map((message) => (
                        <Message key={message.id} message={message} status={message.status} />
                    ))}
                </React.Fragment>
            );
        });
    };

    if (scroll.current) {
        scroll.current.scrollTo({
            behavior: 'smooth',
            top: scroll.current.scrollHeight - scroll.current.clientHeight,
        });
    }

    useEffect(() => {
        if (scroll.current) {
            setTimeout(() => {
                scroll.current.scrollTo({
                    behavior: 'smooth',
                    top: scroll.current.scrollHeight - scroll.current.clientHeight,
                });
            }, 100);
        }
    }, [messages]);
    return (
        <div className="col-lg-9 col-12">
            <div className="editProfileBlock">
                <section className="message-area mt-3">
                    <div className="row">
                        <div className="col-12">
                            <div className="chat-area">
                                <div className="chatbox">
                                    <div className="modal-dialog-scrollable">
                                        <div className="modal-content">
                                            {/* Chat Header */}
                                            <div className="msg-head">
                                                <div className="row">
                                                    <div className="col-8">
                                                        <div className="d-flex align-items-center">
                                                            <ArrowBack sx={{ color: '#FF8008' }} onClick={() => navigate(-1)} />

                                                            {/* Skeleton Loader for Profile Image */}
                                                            {recipientData ? (
                                                                <div className="flex-shrink-0">
                                                                    <img className="img-fluid" src={profileImage} alt="Profile" onError={(e) => (e.target.src = boyImg)} style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                                                                </div>
                                                            ) : (
                                                                <Skeleton variant="circular" width={50} height={50} />
                                                            )}

                                                            <div className="ms-3">
                                                                {/* Skeleton Loader for Name */}
                                                                {recipientData ? (
                                                                    <h3>{recipientData.name}</h3>
                                                                ) : (
                                                                    <Skeleton width="80%" />
                                                                )}

                                                                {/* Skeleton Loader for Last Seen */}
                                                                <p>
                                                                    {recipientData?.lastSeen
                                                                        ? (Date.now() - recipientData.lastSeen.toDate() <= 60 * 1000
                                                                            ? 'Online'
                                                                            : `${timeAgo(recipientData.lastSeen.toDate())}`)
                                                                        : ''}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Typing Indicator */}
                                            {typing && <div className="typing-indicator">User is typing...</div>}

                                            {/* Chat Messages */}
                                            <div className="modal-body">
                                                <div className="msg-body" ref={scroll}>
                                                    <ul>{renderMessages()}</ul>
                                                </div>
                                            </div>

                                            {/* Send Message Box */}
                                            <div className="send-box">
                                                <SendMessage
                                                    conversationId={chatId}
                                                    recipientId={recipientId}
                                                    profile={profile}
                                                    handleTyping={handleTyping}
                                                    updateMessageStatus={updateMessageStatus}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};
